import { useMemo, useReducer } from 'react';
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, FormHelperText, FormLabel, TextField, ThemeProvider, createTheme, } from '@mui/material';
import DragDropImageUpload from '../../components/HelperComponents/DragDropImageUpload';

const initialState = {
    Id: 0,
    Name: "",
    AdUrl: "",
    SlotNumber: 0,
    ViewCount: null
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

/**
 * @param props - all of the functions and properties that The Sponsored Ads dialog needs.
 * @returns - a Dialog which has fields for the user to upload an ad or edit an existing ad.
 * @remarks Sponsored Ad Dialog allows users to set an image, name, and URL for an Ad.
 */
export function SponsoredAdDialog(props) {
    const { showDialog, selectedAd, adFile, handleClose, handleSave, pageText } = props;
    const classes = useStyles();

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#10AD61'
            },
            body1: {
                color: '#00000'
            }
        },
    });

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        Id: selectedAd?.Id,
        Name: selectedAd?.Name,
        AdUrl: selectedAd?.AdUrl,
        SlotNumber: selectedAd?.SlotNumber,
        ViewCount: selectedAd?.ViewCount,
        ad: adFile ? adFile : null
    });

    const { Id, Name, AdUrl, SlotNumber, ViewCount, ad } = state;

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    }

    function isValidHttpUrl(string: string): boolean {
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (err) {
            return false;
        }
    }

    const handleFileUpload = useMemo(() => {
        return (newFile) => {
            setField('ad', newFile);
        };
    }, []);

    const handleSubmit = async () => {
        handleSave(
            {
                SponsoredAdInformation: {
                    Id: 0,
                    Name: Name,
                    AdUrl: AdUrl,
                    SlotNumber: SlotNumber
                },
                SponsoredAd: ad
            }
        )
    }

    const isValidForm = (
        Name &&
        isValidHttpUrl(AdUrl) &&
        ad
    )

    return (
        <div>
            <ThemeProvider theme={theme}>
                <Dialog
                    open={showDialog}
                    onClose={handleClose}
                    fullWidth
                    maxWidth={'md'}

                >
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar sx={{ justifyContent: 'flex-end' }}>
                            <Typography sx={{ flex: 1 }} variant="h6" component="div">
                                {pageText}
                            </Typography>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <Grid alignItems={"flex-start"} justifyContent={'flex-start'}>
                        <Grid
                            container
                            rowSpacing={2}
                            className={classes.subWrapper3}
                            direction={"column"}
                            justifyContent={'flex-start'}
                            alignItems={"flex-start"}
                        >
                            <Grid container direction={"row"} rowSpacing={6} alignItems={"center"} justifyContent={"center"}>
                                <Grid item xs={12}>
                                    <Grid container direction={"column"} alignItems={"center"} justifyContent={"center"}>
                                        <Grid item>
                                            <Typography>
                                                recommended format/size: png 728px by 90px
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <DragDropImageUpload
                                                myFile={ad}
                                                title={Name}
                                                onFileUpload={handleFileUpload}
                                            />
                                        </Grid>
                                        <Grid item xs={12} className={classes.gridPaddingTop} minWidth={"70vh"}>
                                            <Grid container direction={"row"} alignItems={"center"} justifyContent={"center"} columnSpacing={3}>
                                                <Grid item xs={6}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Name</FormLabel>
                                                        <TextField
                                                            value={Name}
                                                            onChange={(event) => setField('Name', event.target.value)}
                                                        />
                                                        <FormHelperText>
                                                            Only displayed on management screen
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>URL</FormLabel>
                                                        <TextField
                                                            value={AdUrl}
                                                            onChange={(event) => setField('AdUrl', event.target.value)}
                                                        />
                                                        <FormHelperText>
                                                            Make sure to test URLs
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} alignSelf={"center"} className={classes.btnContainer} >
                                <Grid container direction={"column"} rowSpacing={3}>
                                    <Grid item xs={7} md={7}>
                                        <Button onClick={handleSubmit} disabled={!isValidForm} variant="contained" fullWidth>
                                            {adFile ? "Update Ad" : "Upload Ad"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Dialog >
            </ThemeProvider>
        </div >
    );
}